/**
 * Tenant Service
 *
 * Purpose: Handles tenant management operations including CRUD operations,
 *          feature toggle management, and license usage tracking.
 *
 * Dependencies: apiService, tenant types
 * Author: Development Team
 * Created: 2025-01-27
 */

import {
  TenantDetails,
  TenantCreateRequest,
  TenantUpdateRequest,
  FeatureToggleUpdateRequest,
  LicenseUsage,
  TenantSearchRequest,
  TenantStatus,
} from '../types/tenant';
import { ApiResponse } from '../types/auth';
import { apiService } from './api';

class TenantService {
  constructor() {
    // Use centralized apiService instead of creating separate Axios instance
  }

  /**
   * Get all tenants with optional filtering
   *
   * Purpose: Retrieves list of all tenants for admin users
   * Input: Optional status and org_id filters
   * Output: List of tenant information
   */
  async getAllTenants(statusFilter?: TenantStatus, orgId?: string): Promise<TenantDetails[]> {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    if (orgId) params.append('org_id', orgId);

    const response = await apiService.api.get<TenantDetails[]>(`/tenants?${params.toString()}`);
    return response.data;
  }

  /**
   * Get specific tenant details
   *
   * Purpose: Retrieves detailed information for a specific tenant
   * Input: tenant_id
   * Output: Tenant information
   */
  async getTenant(tenantId: string): Promise<TenantDetails> {
    const response = await apiService.api.get<TenantDetails>(`/tenants/${tenantId}`);
    return response.data;
  }

  /**
   * Create a new tenant
   *
   * Purpose: Creates a new tenant with specified configuration
   * Note: tenant_id is auto-generated from name by backend
   * Input: Tenant creation data
   * Output: Success confirmation with tenant ID
   */
  async createTenant(tenantData: TenantCreateRequest): Promise<ApiResponse<{ tenant_id: string }>> {
    const response = await apiService.api.post<ApiResponse<{ tenant_id: string }>>(
      '/tenants',
      tenantData
    );
    return response.data;
  }

  /**
   * Update tenant configuration
   *
   * Purpose: Updates tenant with provided changes
   * Input: tenant_id and update data
   * Output: Success confirmation
   */
  async updateTenant(
    tenantId: string,
    updateData: TenantUpdateRequest
  ): Promise<ApiResponse<{ tenant_id: string }>> {
    const response = await apiService.api.put<ApiResponse<{ tenant_id: string }>>(
      `/tenants/${tenantId}`,
      updateData
    );
    return response.data;
  }

  /**
   * Disable a tenant (soft delete)
   *
   * Purpose: Disables tenant instead of hard delete for data integrity
   * Input: tenant_id
   * Output: Success confirmation
   * @deprecated Use deleteTenant with confirmation instead
   */
  async disableTenant(tenantId: string): Promise<ApiResponse<{ tenant_id: string }>> {
    const response = await apiService.api.delete<ApiResponse<{ tenant_id: string }>>(
      `/tenants/${tenantId}`
    );
    return response.data;
  }

  /**
   * Delete a tenant (soft delete with confirmation)
   *
   * Purpose: Soft deletes tenant with name confirmation for safety
   * Input: tenant_id and confirmation name (must match tenant name exactly)
   * Output: Success confirmation
   */
  async deleteTenant(
    tenantId: string,
    confirmationName: string
  ): Promise<ApiResponse<{ tenant_id: string }>> {
    const response = await apiService.api.delete<ApiResponse<{ tenant_id: string }>>(
      `/tenants/${tenantId}`,
      { params: { confirm: confirmationName } }
    );
    return response.data;
  }

  /**
   * Get feature toggles for a tenant
   *
   * Purpose: Retrieves enabled features for tenant
   * Input: tenant_id
   * Output: List of enabled features
   */
  async getTenantFeatures(tenantId: string): Promise<
    ApiResponse<{
      tenant_id: string;
      feature_toggles: string[];
    }>
  > {
    const response = await apiService.api.get<
      ApiResponse<{
        tenant_id: string;
        feature_toggles: string[];
      }>
    >(`/tenants/${tenantId}/feature-toggles`);
    return response.data;
  }

  /**
   * Update feature toggles for a tenant
   *
   * Purpose: Updates the list of enabled features for tenant
   * Input: tenant_id and feature list
   * Output: Success confirmation
   */
  async updateTenantFeatures(
    tenantId: string,
    featureData: FeatureToggleUpdateRequest
  ): Promise<ApiResponse<{ tenant_id: string }>> {
    const response = await apiService.api.put<ApiResponse<{ tenant_id: string }>>(
      `/tenants/${tenantId}/feature-toggles`,
      featureData
    );
    return response.data;
  }

  /**
   * Get license usage statistics for tenant
   *
   * Purpose: Provides license utilization information
   * Input: tenant_id
   * Output: License usage statistics
   */
  async getTenantLicenseUsage(tenantId: string): Promise<LicenseUsage> {
    const response = await apiService.api.get<LicenseUsage>(`/tenants/${tenantId}/license-usage`);
    return response.data;
  }

  /**
   * Search tenants with filters
   *
   * Purpose: Searches tenants with various filters
   * Input: Search criteria
   * Output: Filtered list of tenants
   */
  async searchTenants(searchRequest: TenantSearchRequest): Promise<TenantDetails[]> {
    const params = new URLSearchParams();

    if (searchRequest.query) params.append('query', searchRequest.query);
    if (searchRequest.status) params.append('status', searchRequest.status);
    if (searchRequest.org_id) params.append('org_id', searchRequest.org_id);
    if (searchRequest.limit) params.append('limit', searchRequest.limit.toString());
    if (searchRequest.offset) params.append('offset', searchRequest.offset.toString());

    const response = await apiService.api.get<TenantDetails[]>(`/tenants?${params.toString()}`);
    return response.data;
  }

  /**
   * Check if current user has access to specific feature
   *
   * Purpose: Feature-based authorization for UI elements
   * Input: feature name
   * Output: Boolean indicating feature access
   */
  hasFeature(feature: string): boolean {
    const user = this.getCurrentUserFromStorage();
    return user?.feature_toggles?.includes(feature) || false;
  }

  /**
   * Get current user's tenant information
   *
   * Purpose: Retrieves tenant context for current user
   * Output: Tenant information or null
   */
  getCurrentTenant(): TenantDetails | null {
    const user = this.getCurrentUserFromStorage();
    return user?.tenant || null;
  }

  /**
   * Get current user from localStorage
   *
   * Purpose: Helper method to get user data from storage
   * Output: User object or null
   */
  private getCurrentUserFromStorage(): any {
    try {
      const userStr = localStorage.getItem('orchestratehq_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  }

  /**
   * Validate tenant ID format
   *
   * Purpose: Client-side validation for tenant ID format
   * Input: tenant_id string
   * Output: Boolean indicating if valid
   */
  validateTenantId(tenantId: string): boolean {
    // Tenant ID validation: 3-100 chars, alphanumeric with hyphens/underscores, no spaces
    const regex = /^[a-zA-Z0-9_-]{3,100}$/;
    return regex.test(tenantId) && !tenantId.includes(' ');
  }

  /**
   * Get feature toggle categories for UI organization
   *
   * Purpose: Organizes features into categories for better UX
   * Output: Feature categories with descriptions
   */
  getFeatureCategories() {
    return {
      core: {
        name: 'Core Features',
        description: 'Essential platform functionality',
        features: [
          'ai_planning',
          'advanced_analytics',
          'workflow_automation',
          'multi_currency',
          'advanced_reporting',
        ],
      },
      beta: {
        name: 'Beta Features',
        description: 'Experimental and preview features',
        features: [
          'beta_features',
          'experimental_ai_assistant',
          'predictive_analytics',
          'smart_recommendations',
        ],
      },
      integrations: {
        name: 'API Integrations',
        description: 'Third-party platform integrations',
        features: [
          'api_mediaocean',
          'api_quickbooks',
          'api_facebook',
          'api_google_ads',
          'api_tiktok',
        ],
      },
      advanced: {
        name: 'Advanced Features',
        description: 'Premium and advanced capabilities',
        features: [
          'real_time_analytics',
          'custom_dashboards',
          'bulk_operations',
          'advanced_permissions',
          'audit_trail_enhanced',
        ],
      },
      performance: {
        name: 'Performance Features',
        description: 'Performance optimization features',
        features: ['caching_enabled', 'compression_enabled', 'batch_processing'],
      },
      security: {
        name: 'Security Features',
        description: 'Enhanced security capabilities',
        features: ['mfa_required', 'ip_whitelisting', 'advanced_encryption'],
      },
      ux: {
        name: 'User Experience',
        description: 'UI/UX enhancement features',
        features: ['dark_mode', 'custom_themes', 'accessibility_enhanced', 'mobile_optimized'],
      },
    };
  }
}

export const tenantService = new TenantService();
export default tenantService;
